import React from 'react';
import { connect } from 'react-redux';
import { Route, RouteComponentProps } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { hideRouterError } from '~src/spacesPlatform/actions/RouterErrorActions';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { usePrevious } from '../hooks';
import { AppState } from '../redux/state/AppState';
import { RouterErrorType } from '../redux/state/RouterErrorType';
import { RouterError } from './RouterError';

interface ReduxRouterErrorWrapperOwnProps {
  readonly children: React.ReactNode;
}

interface ReduxRouterErrorWrapperStateProps {
  readonly displayError: boolean;
  readonly errorType: RouterErrorType;
  readonly redirectSiteUrlName: string;
  readonly urls: SpacesSiteUrls;
}

interface ReduxRouterErrorWrapperDispatchProps {
  readonly hideRouterError: () => void;
}

type ReduxRouterErrorWrapperProps = ReduxRouterErrorWrapperOwnProps & ReduxRouterErrorWrapperStateProps & ReduxRouterErrorWrapperDispatchProps;

const ReduxRouterErrorWrapper = React.memo((props: ReduxRouterErrorWrapperProps) => {
  const renderError = React.useCallback(
    (routeProps: RouteComponentProps) => {
      const urls = props.redirectSiteUrlName ? new SpacesSiteUrls({siteUrlName: props.redirectSiteUrlName}) : props.urls;
      return <ReduxRouterError displayError={props.displayError} errorType={props.errorType} urls={urls} hide={props.hideRouterError} path={routeProps.location.pathname} />;
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.errorType]
  );

  if (props.displayError) {
    return (
      <Route render={renderError}/>
    );
  }

  return React.Children.only(props.children) as React.ReactElement<any>;
});

interface ReduxRouterErrorProps {
  readonly hide: () => void;
  readonly displayError: boolean;
  readonly errorType: RouterErrorType;
  readonly urls: SpacesSiteUrls;
  readonly path: string;
}

const ReduxRouterError = React.memo((props: ReduxRouterErrorProps) => {
  const previousPath = usePrevious(props.path);
  React.useEffect(
    () => {
      if (previousPath && previousPath !== props.path) {
        props.hide();
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.path]
  );

  return <RouterError errorType={props.errorType} urls={props.urls} />;
});

const mapStateToProps: MapStateToProps<ReduxRouterErrorWrapperStateProps> = (state: AppState) => ({
  displayError: state.reduxRouterError.displayError,
  errorType: state.reduxRouterError.errorType,
  redirectSiteUrlName: state.reduxRouterError.redirectSiteUrlName,
  urls: siteUrlsSelector(state),
});

const mapDispatchToProps: MapDispatchToProps<ReduxRouterErrorWrapperDispatchProps> = (dispatch) => bindActionCreators({
  hideRouterError: hideRouterError
}, dispatch);

export const ConnectedReduxRouterErrorWrapper = connect(mapStateToProps, mapDispatchToProps)(ReduxRouterErrorWrapper);
